/* eslint-disable max-lines-per-function */
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment } from 'react';
import { RowDivider } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/RowDivider';
import { StyleProps } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/types';
import {
  Avatar,
  Body,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@e2n/ui/v2';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Location } from '@/domain/mitarbeiter/features/rotationsplan/components/Location';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import { KalenderByArbeitsbereicheStation } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/kalenderByArbeitsbereiche/KalenderStation';
import { KalenderArbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/kalenderByArbeitsbereiche/KalenderArbeitsbereich';
import { useHoverPopover } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/useHoverPopover';

export function ArbeitsbereichKalenderStation({
  kalenderStation,
  arbeitsbereich,
  displayYear,
}: {
  kalenderStation: KalenderByArbeitsbereicheStation;
  arbeitsbereich: KalenderArbeitsbereich;
  displayYear: number;
}) {
  const { ref, open, setOpen, cursorX, handleMouseEnter, handleMouseLeave } =
    useHoverPopover();
  const { t } = useTranslation();

  return (
    <div
      className={`col-start-(--col-start) col-end-(--col-end) py-1`}
      style={
        {
          '--col-start': kalenderStation.getColStart(displayYear),
          '--col-end': kalenderStation.getColEnd(displayYear),
        } as StyleProps
      }>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="relative w-full h-full"
        ref={ref}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            style={{
              position: 'absolute',
              left: cursorX,
              bottom: 0,
              height: 0.5,
              width: 0.5,
              color: 'white',
              outline: 'none',
            }}
          />
          <div
            className="w-full h-full bg-(--bg-color) border-(--border-color) border border-solid rounded-lg flex items-center justify-start p-2"
            style={
              {
                '--bg-color': arbeitsbereich.getBorderColor(),
                '--border-color': arbeitsbereich.getBorderColor(),
                '--text-color': arbeitsbereich.getTextColor(),
              } as StyleProps
            }>
            <Body className="truncate text-(--text-color)" size="sm">
              {kalenderStation.mitarbeiter.length}
            </Body>
          </div>
          <PopoverContent
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="flex flex-col gap-2 p-3 bg-backgrounds-primary border-neutrals-v2-200 rounded-xl w-75">
            <div className="flex flex-col mb-2 gap-1">
              <div className="flex gap-2 items-center">
                <FontAwesomeIcon
                  icon={faCircle}
                  color={arbeitsbereich.getBorderColor()}
                  className="size-2.5 shrink-0"
                />
                <Body weight="semibold">{arbeitsbereich.name}</Body>
              </div>
              <Body className="text-labels-secondary" size="sm">
                {dayjs(kalenderStation.station.von).format('DD.MM.YYYY') +
                  '–' +
                  dayjs(kalenderStation.station.bis).format('DD.MM.YYYY') +
                  ' • ' +
                  kalenderStation.mitarbeiter.length +
                  ' ' +
                  t('entities.employee')}
              </Body>
            </div>
            <div className="flex flex-col gap-3">
              {kalenderStation.mitarbeiter.map((m, index) => (
                <Fragment key={m.id}>
                  <div className="flex gap-2 items-center">
                    {m.imgUrl ? (
                      <Avatar
                        src={m.imgUrl}
                        alt={m.vorname + ' ' + m.nachname}
                        fallbackInitials={(
                          m.vorname[0] + m.nachname[0]
                        ).toUpperCase()}
                      />
                    ) : (
                      <Avatar
                        type="initials"
                        initials={(m.vorname[0] + m.nachname[0]).toUpperCase()}
                      />
                    )}
                    <div className="hidden md:flex md:flex-col md:gap-1 min-w-0">
                      <Body className="truncate" size="sm">
                        {m.vorname + ' ' + m.nachname}
                      </Body>
                      <Body className="text-labels-secondary" size="sm">
                        {m.lehrjahr + ' ⋅ ' + m.beruf.abkuerzung}
                      </Body>
                      {kalenderStation.station.abteilungsleiter && (
                        <Body
                          className="flex items-center gap-1 text-labels-secondary"
                          size="sm">
                          <FontAwesomeIcon icon={faUser} />
                          {kalenderStation.station.abteilungsleiter.getName()}
                        </Body>
                      )}
                      <Location
                        className="text-labels-secondary"
                        standort={
                          kalenderStation.station.arbeitsbereich.standort
                        }
                      />
                    </div>
                  </div>
                  {index < kalenderStation.mitarbeiter.length - 1 && (
                    <RowDivider />
                  )}
                </Fragment>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
